import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class GoogleTranslateService {
  private initialized = false;

  constructor() {
    this.init();
  }

  /**
   * Initializes the Google Translate Element widget dynamically
   */
  public init(): void {
    if (typeof window === 'undefined' || this.initialized) return;

    // Define the global callback expected by Google Translate Element
    (window as any).googleTranslateElementInit = () => {
      try {
        if ((window as any).google?.translate?.TranslateElement) {
          new (window as any).google.translate.TranslateElement(
            {
              pageLanguage: 'en',
              includedLanguages: 'en,th',
              autoDisplay: false,
              layout: (window as any).google.translate.TranslateElement.InlineLayout?.HORIZONTAL,
            },
            'google_translate_element'
          );
        }
      } catch (err) {
        console.warn('Google Translate initialization warning:', err);
      }
    };

    // Ensure hidden container exists
    if (!document.getElementById('google_translate_element')) {
      const container = document.createElement('div');
      container.id = 'google_translate_element';
      container.style.display = 'none';
      document.body.appendChild(container);
    }

    // Inject Google Translate script if not already present
    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.type = 'text/javascript';
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    }

    this.injectStyles();
    this.initialized = true;
  }

  /**
   * Switch the page language dynamically between 'en' and 'th'
   */
  public translatePage(lang: 'en' | 'th'): void {
    this.init();

    const target = lang === 'th' ? 'th' : 'en';
    const cookieVal = `/en/${target}`;

    // 1. Set Google Translate cookie
    this.setGoogleCookie(cookieVal);

    // 2. Trigger the Google Translate select dropdown element in DOM
    this.triggerCombo(target);
  }

  /**
   * Set googtrans cookie on root and domains
   */
  private setGoogleCookie(value: string): void {
    const expires = 'expires=Fri, 31 Dec 9999 23:59:59 GMT';
    document.cookie = `googtrans=${value}; path=/; ${expires};`;

    try {
      const hostname = window.location.hostname;
      document.cookie = `googtrans=${value}; path=/; domain=${hostname}; ${expires};`;

      const parts = hostname.split('.');
      if (parts.length > 1) {
        const rootDomain = '.' + parts.slice(-2).join('.');
        document.cookie = `googtrans=${value}; path=/; domain=${rootDomain}; ${expires};`;
      }
    } catch (e) {
      // Ignore domain error in restricted environments
    }
  }

  /**
   * Attempt to find and update Google Translate select element
   */
  private triggerCombo(targetLang: string, attempts = 0): void {
    const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');
    if (combo) {
      combo.value = targetLang;
      combo.dispatchEvent(new Event('change'));
      return;
    }

    if (attempts < 15) {
      setTimeout(() => this.triggerCombo(targetLang, attempts + 1), 200);
    }
  }

  /**
   * Inject CSS to hide default Google Translate toolbar, banners, and tooltips
   */
  private injectStyles(): void {
    const styleId = 'google-translate-custom-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .goog-te-banner-frame.skiptranslate,
      .goog-te-banner-frame,
      iframe.goog-te-banner-frame {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
      }
      body {
        top: 0px !important;
        position: static !important;
      }
      #goog-gt-tt,
      .goog-te-balloon-frame {
        display: none !important;
      }
      .goog-text-highlight {
        background: none !important;
        box-shadow: none !important;
      }
      .skiptranslate:not(.lang-pill) {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }
}
